import React, { FormEvent, useEffect, useState } from "react";
import Papa from "papaparse";
import PreviewCSV from "./components/PreviewCSV";
import {
  bulkRegistration,
  resetDcddUserDetails,
} from "../../users/state/action";
import { connect, useSelector } from "react-redux";
import {
  BulkRegistartionTemplate,
  bulkRegistrationProp,
} from "../store/ActionType";
import { stateStatusType } from "modules/modules/store/actionType";
import { ApiError, CustomTooltip } from "components";
import { DownloadIcon } from "assets";

type DispatchProps = ReturnType<typeof mapDispatchToProps>;
type Props = DispatchProps;
const BulkRegister: React.FC<Props> = ({
  onBulkRegistartion,
  onResetDcddDetails,
}) => {
  const [csvData, setCsvData] = useState<bulkRegistrationProp[]>([]);
  const [csvUrl, setCsvUrl] = useState<File | null>(null);
  const [csvError, setCsvError] = useState("");
  const [fileName, setFileName] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const dcddCreateState = useSelector(
    (state: any) => state.dcddUser.genericDcddUserState
  );
  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setCsvUrl(file);
    setFileName(file.name);
    setCsvError("");
    Papa.parse<bulkRegistrationProp>(file, {
      header: true,
      skipEmptyLines: true,
      complete: (results) => {
        setCsvData(results.data);
      },
      error: (error) => {
        console.error("CSV parsing error:", error);
        setCsvError(error.message);
        setCsvData([]);
      },
    });
  };
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [progress, setProgress] = useState(0);
  const [apiError, setApiError] = useState(null);
  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!csvData.length) return;

    setIsSubmitting(true);
    setProgress(0);
    let uploadedFilename: string | undefined = undefined;

    if (csvUrl) {
      const uploadUrl = `${process.env.REACT_APP_CSV_UPLOAD_BASE_URL}`;
      const formData = new FormData();
      formData.append("file", csvUrl);

      try {
        const xhr = new XMLHttpRequest();

        xhr.open("POST", uploadUrl ?? "", true);

        xhr.upload.onprogress = (event) => {
          if (event.lengthComputable) {
            const percentComplete = Math.round(
              (event.loaded / event.total) * 100
            );
            setProgress(percentComplete);
          }
        };

        xhr.onload = async () => {
          if (xhr.status === 200) {
            const result = JSON.parse(xhr.responseText);
            uploadedFilename = result?.filePath;

            if (!uploadedFilename) {
              console.error("CSV upload response invalid", result);
              throw new Error("CSV upload response invalid");
            }

            await onBulkRegistartion(uploadedFilename);
            setIsSubmitting(false);
            setProgress(100);
            setSuccessMessage("Registration was done successfully!");
          } else {
            console.error("Upload failed:", xhr.responseText);
            throw new Error("Failed to upload CSV");
          }
        };

        xhr.onerror = () => {
          console.error("Upload error:", xhr.responseText);
          setIsSubmitting(false);
          throw new Error("Upload failed due to a network error.");
        };

        xhr.send(formData);
      } catch (err) {
        setIsSubmitting(false);
        return;
      }
    }
  };
  useEffect(() => {
    onResetDcddDetails();
    if (!dcddCreateState.isLoading && isSubmitting) {
      if (dcddCreateState.status === stateStatusType.FAILED) {
        setApiError(dcddCreateState.error);
      } else if (dcddCreateState.status === stateStatusType.SUCCESS) {
      }
    }
  }, [dcddCreateState.isLoading, dcddCreateState.error, dcddCreateState.status, isSubmitting, onResetDcddDetails]);
  const downloadCSVHeaders = () => {
    const csvContent = BulkRegistartionTemplate.join(",") + "\n";
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });

    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = "bulk_registration_template.csv";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div className="p-8 w-full mx-auto">
      <form>
        <h2 className="text-2xl font-bold mb-6 text-gray-800">
          📄 Bulk User Registration
        </h2>

        <div className="flex  items-center flex-col sm:flex-row gap-4 mb-6">
          <div className="mb-6">
            <label
              htmlFor="csv_path"
              className="cursor-pointer inline-block bg-primary-500 hover:bg-primary-900 text-white font-medium py-2 px-4 rounded transition duration-300"
            >
              Upload CSV File
            </label>
            <input
              id="csv_path"
              name="csv_path"
              type="file"
              accept=".csv"
              onChange={handleFileUpload}
              className="hidden"
            />
          </div>
          <div className="mb-6">
            <CustomTooltip
              title="Download bulk registration template in csv"
              placement="bottom"
            >
              <button
                type="button"
                onClick={downloadCSVHeaders}
                className="flex items-center cursor-pointer  bg-green-500 hover:bg-green-700 text-white font-medium py-2 px-4 rounded transition duration-300"
              >
                <DownloadIcon className="size-4 mr-1" /> CSV Template
              </button>
            </CustomTooltip>
          </div>
        </div>
        {csvData.length > 0 || csvError ? (
          <PreviewCSV
            csvData={csvData}
            fileName={fileName}
            csvError={csvError}
            handleSubmit={handleSubmit}
            isSubmitting={isSubmitting}
            progress={progress}
          />
        ) : (
          <p className="text-gray-600">
            No file uploaded yet. Please upload a CSV file to see the preview.
          </p>
        )}
        {apiError && dcddCreateState.status === stateStatusType.FAILED && <ApiError>{apiError}</ApiError>}
        {successMessage &&dcddCreateState.status ===stateStatusType.SUCCESS && (
          <div className="bg-primary-400 text-white shadow-md px-4 py-2 rounded-md">
            {successMessage}
          </div>
        )}
      </form>
    </div>
  );
};

const mapDispatchToProps = (dispatch: any) => ({
  onBulkRegistartion: (csv_path?: string) =>
    dispatch(bulkRegistration(csv_path)),
  onResetDcddDetails: () => resetDcddUserDetails(),
});

export default connect(null, mapDispatchToProps)(BulkRegister);
